import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const AutoBackupTab = () => {
  const [backupSettings, setBackupSettings] = useState({ enabled: false, frequency: 'daily', time: '02:00', retention_count: 7, backup_path: '/backup' });
  const [backupSchedulerStatus, setBackupSchedulerStatus] = useState(null);
  const [backupList, setBackupList] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);

  useEffect(() => {
    loadBackupSchedulerStatus();
    loadBackupList();
  }, []);

  const loadBackupSchedulerStatus = async () => {
    try {
      const response = await api.get('/admin/backup/scheduler/status');
      const data = response.data;
      if (data.success) {
      setBackupSchedulerStatus(data.data);
      setBackupSettings(prev => ({
        ...prev,
        enabled: data.data.is_running,
        backup_path: data.data.backup_dir || prev.backup_path,
        time: data.data.backup_time || prev.time
      }));
      }
    } catch (err) {
      console.error('Blad podczas pobierania statusu schedulera:', err);
    }
  };

  const loadBackupList = async () => {
    try {
      const response = await api.get('/admin/backup/list');
      const data = response.data;
      if (data.success) {
      setBackupList(data.data.backups || []);
      }
    } catch (err) {
      console.error('Blad podczas pobierania listy backupow:', err);
    }
  };

  const saveBackupSettings = async () => {
    try {
      const response = await api.post('/admin/backup/settings', backupSettings);
      const data = response.data;
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(`Błąd: ${data.message}`);
      }
    } catch (err) {
      toast.error(`Błąd: ${err.message}`);
    }
  };

  const createManualBackup = async () => {
    try {
      const response = await api.post('/admin/backup/manual');
      const data = response.data;
      if (data.success) {
        toast.success(`Backup utworzony! Plik: ${data.data.backup_filename}, Rozmiar: ${(data.data.file_size / 1024).toFixed(1)} KB`);
        loadBackupList();
      } else {
        toast.error(`Błąd: ${data.message}`);
      }
    } catch (err) {
      toast.error(`Błąd: ${err.message}`);
    }
  };

  const testBackupSystem = async () => {
    try {
      const response = await api.get('/admin/backup/scheduler/status');
      const data = response.data;
      if (data.success) {
      const status = data.data;
      toast(`Status systemu backup: Scheduler: ${status.is_running ? 'Aktywny' : 'Nieaktywny'}, Następny backup: ${status.next_backup || 'Nie zaplanowany'}, Kopii w systemie: ${status.total_backups}`);
      }
    } catch (err) {
      toast.error(`Błąd: ${err.message}`);
    }
  };

  const deleteBackup = async (filename) => {
    if (window.confirm(`Czy na pewno chcesz usunąć kopię:\n${filename}?`)) {
      try {
        const response = await api.delete(`/admin/backup/delete/${encodeURIComponent(filename)}`);
        const data = response.data;
        if (data.success) {
          toast.success('Kopia została usunięta');
          loadBackupList();
        } else {
          toast.error(`Błąd: ${data.message}`);
        }
      } catch (err) {
        toast.error(`Błąd: ${err.message}`);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const downloadBackup = async (backup) => {
    try {
      const response = await api.get(
        `/admin/backup/download/${encodeURIComponent(backup.filename)}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(`Błąd pobierania: ${err.message}`);
    }
  };

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {/* Status automatycznych kopii zapasowych */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
            🔄 Automatyczne kopie zapasowe
          </h3>
          <span style={{
            padding: '0.5rem 1rem',
            backgroundColor: backupSettings?.enabled ? 'var(--success-light)' : 'var(--danger-light)',
            color: backupSettings?.enabled ? 'var(--success)' : 'var(--danger)',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {backupSettings?.enabled ? '🟢 Aktywne' : '🔴 Nieaktywne'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Częstotliwość kopii
            </label>
            <select
              value={backupSettings?.frequency || 'daily'}
              onChange={(e) => setBackupSettings({...backupSettings, frequency: e.target.value})}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            >
              <option value="hourly">Co godzinę</option>
              <option value="daily">Codziennie</option>
              <option value="weekly">Co tydzień</option>
              <option value="monthly">Co miesiąc</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Godzina wykonania
            </label>
            <input
              type="time"
              value={backupSettings?.time || '02:00'}
              onChange={(e) => setBackupSettings({...backupSettings, time: e.target.value})}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Liczba kopii do zachowania
            </label>
            <input
              type="number"
              value={backupSettings?.retention_count || 7}
              onChange={(e) => setBackupSettings({...backupSettings, retention_count: parseInt(e.target.value) || 7})}
              min="1"
              max="30"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Ścieżka kopii zapasowych
            </label>
            <input
              type="text"
              value={backupSettings?.backup_path || '/backup'}
              onChange={(e) => setBackupSettings({...backupSettings, backup_path: e.target.value})}
              placeholder="/backup"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={backupSettings?.enabled || false}
              onChange={(e) => setBackupSettings({...backupSettings, enabled: e.target.checked})}
            />
            <span style={{ fontSize: '0.875rem' }}>Włącz automatyczne kopie zapasowe</span>
          </label>
        </div>

        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={saveBackupSettings}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--info)',
              color: 'var(--info)',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            💾 Zapisz ustawienia
          </button>
          <button
            onClick={createManualBackup}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--success)',
              color: 'var(--success)',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            📦 Utwórz kopię teraz
          </button>
          <button
            onClick={testBackupSystem}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--warning)',
              color: 'var(--warning)',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            🧪 Testuj system
          </button>
        </div>
      </div>

      {/* Lista kopii zapasowych */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '1rem',
          backgroundColor: 'var(--bg)',
          borderBottom: '1px solid var(--border)',
          fontWeight: '600',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>📁 Lista kopii zapasowych</span>
          <button
            onClick={loadBackupList}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--info)',
              color: 'var(--info)',
              fontSize: '12px'
            }}
          >
            🔄 Odśwież
          </button>
        </div>

        <div className="admin-table-wrap" style={{ overflow: 'auto', maxHeight: '400px' }}>
          {backupList.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: 'var(--text-muted)'
            }}>
              <p>Brak kopii zapasowych</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: 'var(--bg)', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Nazwa pliku
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Data utworzenia
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Rozmiar
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Typ
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody>
                {backupList.map((backup, index) => (
                  <tr key={backup.filename || index} style={{ backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: '500' }}>
                        {backup.filename}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {backup.path}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      {new Date(backup.created_at).toLocaleString('pl-PL')}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>
                      {formatFileSize(backup.size)}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: backup.type === 'automatic' ? 'var(--success-light)' : 'var(--warning-light)',
                        color: backup.type === 'automatic' ? 'var(--success)' : 'var(--warning)',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {backup.type === 'automatic' ? 'Automatyczna' : 'Manualna'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => downloadBackup(backup)}
                          style={{
                            padding: '4px 10px',
                            border: '1px solid var(--info)',
                            color: 'var(--info)',
                            fontSize: '11px'
                          }}
                        >
                          📥 Pobierz
                        </button>
                        <button
                          onClick={() => deleteBackup(backup.filename)}
                          style={{
                            padding: '4px 10px',
                            border: '1px solid var(--danger)',
                            color: 'var(--danger)',
                            fontSize: '11px'
                          }}
                        >
                          🗑️ Usuń
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoBackupTab;
